
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Tabular
 *
 * @file Tabular.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  TableHead, TableBody,
  Typography,
  TablePagination, createTheme, ThemeProvider, useTheme
} from '@mui/material';
// import Pagination from './components/Pagination';
import {
  Root,
  TitleContainer,
  TitleContainerLeft,
  TitleContainerRight,
  TableContainerRoot,
  TabularTable,
  TableHeadRowRoot,
  HeaderTableCell,
  TableCellContent,
  HeaderTableCellContent,
  PaddingCheckbox,
  HeaderPaddingCheckbox,
  SortIcon,
  TableBodyRowRoot,
  BodyTableCell,
  SelectionSummary,
  SelectionCount,
  SelectionLabel,
  DropButton,
  InfoHeaderIconContainer
} from './styles/TabularStyles';
import {
  OverviewTemplate,
  TextTemplate,
  BadgeTemplate,
  ProgressTemplate,
  TextChipTemplate,
  ContentTemplate,
  DateTemplate,
  ComprehensionTemplate,
  Info,
  DropdownTemplate
} from './components/Template';
import SearchBox from '../common/SearchBox';
import useAdvancedSearch from '../../hooks/useAdvancedSearch';
import Icon from '../icons/Icon';
import useSort from '../../hooks/useSort';
import useSelection from '../../hooks/useSelection';
import usePrevious from '../../hooks/usePervious';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import ToolTipWrapper from '../tooltip/TooltipWrapper';
import Utils from '../../common/utils';
import CheckboxOutlineIcon from '../icons/CheckboxOutlineIcon';
import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon';
import TableFilter from '../common/TableFilter';
import useTableFilter from '../../hooks/useTableFilter';
import {
  ARIA_SORT_COLUMN_FIELDS,
  ARIA_SORT_VALUE,
  TABULAR_SORT_DIRECTION
} from '../../common/constants';
import { dataAttributesPropType } from '../../common/dataAttributes';

const getColumnHeaderAriaSort = (column, sortConfig) => {
  if (!column?.isSortable || !column?.sort) {
    return undefined;
  }

  const [field] = column.sort;

  if (!ARIA_SORT_COLUMN_FIELDS.includes(field)) {
    return undefined;
  }

  if (sortConfig?.field !== field) {
    return ARIA_SORT_VALUE.NONE;
  }

  return sortConfig.direction === TABULAR_SORT_DIRECTION.ASC
    ? ARIA_SORT_VALUE.ASCENDING
    : ARIA_SORT_VALUE.DESCENDING;
};

const Tabular = (props) => {
  const {
    title,
    textInfo,
    columns,
    data,
    config,
    callback,
    onSelect,
    onRowClick,
    selectionDisableName = 'isStarted',
    onInit = () => null,
    isRowDisabled = () => false,
    components: componentsProp = {},
    classes = {}
  } = props;

  const RootComponent = componentsProp.root || Root;
  const TableHeadRowComponent = componentsProp.tableRowRoot || TableHeadRowRoot;
  const HeaderTableCellComponent = componentsProp.tableThCell || HeaderTableCell;
  const BodyTableCellComponent = componentsProp.tableCell || BodyTableCell;
  const TitleContainerRightComponent = componentsProp.titleContainerRight || TitleContainerRight;
  const SortIconComponent = componentsProp.sortIcon || SortIcon;
  const PaddingCheckboxComponent = componentsProp.paddingCheckbox || PaddingCheckbox;
  const hasSharedCheckbox = Boolean(
    componentsProp.paddingCheckbox || classes.paddingCheckbox
  );
  const HeaderPaddingCheckboxComponent = hasSharedCheckbox
    ? PaddingCheckboxComponent
    : HeaderPaddingCheckbox;

  useEffect(() => {
    onInit();
  }, []);

  const {
    showPagination = false,
    showSearch = false,
    showTitle = false,
    showCheckBox = false,
    showColumnDivider = false,
    searchPlaceholder,
    searchDataAttributes,
    showSelectionSummary,
    showButton,
    filterByText,
    showFilter,
    tableClassName,
    filterConfig,
    dropButtonLabel,
    rowVariant
  } = config || {};

  // Rows read as individual cards floating on a tinted panel (Manage roster).
  const isCardRow = rowVariant === 'card';

  const {
    handleDropButtonClick,
    onHandleSearch,
    onHandleFilter
  } = callback || {};

  const cardWrapperClass = isCardRow ? 'cardTableWrapper' : '';
  const cardTableClass = isCardRow ? 'cardTable' : '';
  const cardCellClass = isCardRow ? 'cardCell' : '';
  const thVariantClass = isCardRow ? 'cardThCell' : '';
  const [page, setPage] = useState(0);
  const previousConfig = usePrevious(config);
  const { record,
    allItemsChecked,
    someItemsChecked,
    onSelectAndUnSelectItem,
    handleSelectAndUnSelectAll,
    resetItems,
    allItemsDisabled } = useSelection(data, onSelect, selectionDisableName);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const { search, setSearch } = useAdvancedSearch(record, { columns });
  const rowsPerPage = data.length;

  const filteredData = useTableFilter({
    data: record,
    search,
    columns,
    filterConfig,
    selectedFilter
  });
  const selectedRows = filteredData.filter(i => i.checked);
  const selectedCount = selectedRows.length;
  const totalCount = filteredData.filter(
    item => !item[selectionDisableName]
  ).length;
  const isDropButtonDisabled = selectedCount === 0;

  // Ref and state for reading CSS variable
  const containerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(500);

  // Read CSS variable and convert to number
  useEffect(() => {
    if (containerRef.current) {
      const computedStyle = getComputedStyle(containerRef.current);
      const cssVarValue = computedStyle.getPropertyValue('--table-container-max-height').trim();

      if (cssVarValue) {
        // Remove 'px' and convert to number
        const heightValue = parseInt(cssVarValue.replace('px', ''), 10);
        if (!Number.isNaN(heightValue)) {
          setMaxHeight(heightValue);
        }
      }
    }
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const outerTheme = useTheme();
  const theme = useMemo(() => createTheme(outerTheme, {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: 8
          }
        }
      }
    }
  }), [outerTheme]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const { sortedData, sortConfig, setSortConfig } = useSort(filteredData);
  const visibleRows = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleRowClick = (event, item) => {
    event.preventDefault();

    onRowClick(item);
  };
  const renderCell = (column, item) => {
    let renderData = null;
    try {
      renderData = column.render(item);
    // eslint-disable-next-line no-unused-vars
    } catch (error) {
      renderData = null;
    }

    return renderData;
  };

  useEffect(() => {
    if (JSON.stringify(previousConfig) !== JSON.stringify(config)) {
      resetItems();
    }
  }, [config, previousConfig, resetItems]);

  return (
    <ThemeProvider theme={theme}>
      <RootComponent
        ref={containerRef}
        elevation={0}
        className={classes.root || ''}
      >
        <TitleContainer>
          {showTitle && (
            <TitleContainerLeft>
              <Typography variant="h6" gutterBottom>
                {title}
              </Typography>
            </TitleContainerLeft>
          )}
          {showSearch && (
            <TitleContainerRightComponent className={classes.titleContainerRight || ''}>
              <SearchBox
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  onHandleSearch?.(value);
                }}
                placeholder={searchPlaceholder}
                dataAttributes={searchDataAttributes}
              />
            </TitleContainerRightComponent>
          )}
          {showFilter && filterConfig && (
          <TableFilter
            components={{ filterSelect: componentsProp.filterSelect }}
            filterSelectClassName={classes.filterSelect || ''}
            filterByText={filterByText}
            filterConfig={filterConfig}
            value={selectedFilter}
            onChange={(value) => {
              setSelectedFilter(value);
              setPage(0);
              onHandleFilter?.(value);
            }}
          />
          )}
        </TitleContainer>
        <TableContainerRoot className={cardWrapperClass}>
          <CustomScrollbar
            autoHeight
            autoHeightMax={maxHeight}
          >
            <TabularTable
              stickyHeader
              className={[cardTableClass, tableClassName || ''].filter(Boolean).join(' ')}
            >
              <TableHead>
                <TableHeadRowComponent className={isCardRow ? 'cardHeaderRowRoot' : ''}>
                  {columns.map((column, index) => {
                    const sanitizedTitle = Utils.sanitizeString(column?.title);
                    const headerAriaSort = getColumnHeaderAriaSort(column, sortConfig);

                    return (
                      <HeaderTableCellComponent
                        key={column.title}
                        scope="col"
                        {...(headerAriaSort ? { 'aria-sort': headerAriaSort } : {})}
                        className={[
                          thVariantClass,
                          !column?.sort ? 'cursor' : '',
                          column.cellClassName || ''
                        ].filter(Boolean).join(' ')}
                      >
                        <HeaderTableCellContent id={`select-${sanitizedTitle}`}>
                          {showCheckBox && index === 0 && (
                            <ToolTipWrapper
                              tooltip={textInfo.tooltip.allItemsSelectionDisabled}
                              showTooltip={allItemsDisabled}
                              placement="top"
                            >
                              <span>
                                <HeaderPaddingCheckboxComponent
                                  className={classes.paddingCheckbox || ''}
                                  indeterminate={!allItemsChecked && someItemsChecked}
                                  checked={allItemsChecked}
                                  onChange={handleSelectAndUnSelectAll}
                                  disabled={allItemsDisabled}
                                  inputProps={{
                                    'aria-labelledby': `select-${sanitizedTitle}`
                                  }}
                                  icon={<CheckboxOutlineIcon />}
                                  checkedIcon={<CheckboxCheckedIcon />}
                                />
                              </span>
                            </ToolTipWrapper>
                          )}
                          {column.title}

                          {column?.info ? (
                            <Info
                              {...column.info}
                              infoIconContainerClass={InfoHeaderIconContainer}
                            />
                          ) : null}

                          {column.isSortable && column.sort && (
                            <SortIconComponent
                              className={classes.sortIcon || ''}
                              size="small"
                              type="button"
                              aria-label={column.title}
                              onClick={() => {
                                const [field, type] = column.sort;
                                const direction =
                                  // eslint-disable-next-line no-nested-ternary
                                  sortConfig?.field === field
                                    ? sortConfig.direction === 'asc'
                                      ? 'desc'
                                      : 'asc'
                                    : 'desc';
                                setSortConfig({ field, type, direction });
                              }}
                            >
                              <Icon name="SortIcon" />
                            </SortIconComponent>
                          )}
                        </HeaderTableCellContent>
                      </HeaderTableCellComponent>
                    );
                  })}
                </TableHeadRowComponent>
              </TableHead>
              <TableBody>
                {visibleRows.map((item, idx) => {
                  const rowDisabled = isRowDisabled(item);
                  const isLastRow = idx === visibleRows.length - 1;

                  return (
                    <TableBodyRowRoot
                      key={idx}
                      className={[
                        isCardRow ? 'cardBodyRowRoot' : '',
                        rowDisabled ? 'disabledRow' : '',
                        isCardRow && item.checked && !rowDisabled ? 'selectedRow' : '',
                        item.hasError || item.requiresManualDateEntry || item.hasNoChaptersError
                          ? 'errorRow'
                          : '',
                        isLastRow ? 'tableBodyRowLast' : ''
                      ].filter(Boolean).join(' ')}
                      onClick={(event) => {
                        if (rowDisabled) return;
                        handleRowClick(event, item);
                      }}
                    >
                      {columns.map((column, index) => (
                        <BodyTableCellComponent
                          key={column.title + index}
                          className={[
                            !showColumnDivider ? 'tableBorder' : '',
                            cardCellClass,
                            column.cellClassName || ''
                          ].filter(Boolean).join(' ')}
                        >
                          <TableCellContent className={isCardRow ? 'cardCellContent' : ''}>
                            {showCheckBox && index === 0 && (
                            <ToolTipWrapper
                              tooltip={textInfo.tooltip.selectionDisabled}
                              showTooltip={item[selectionDisableName] || false}
                              placement="top"
                            >
                              <span>
                                <PaddingCheckboxComponent
                                  className={classes.paddingCheckbox || ''}
                                  checked={item.checked}
                                  disabled={item[selectionDisableName] || false}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                  onChange={(event) => {
                                    onSelectAndUnSelectItem(
                                      item.id,
                                      event.target.checked
                                    );
                                  }}
                                  inputProps={{
                                    'aria-labelledby': `select-${Utils.sanitizeString(item?.title)}`
                                  }}
                                  icon={<CheckboxOutlineIcon />}
                                  checkedIcon={<CheckboxCheckedIcon />}
                                />
                              </span>
                            </ToolTipWrapper>
                            )}
                            {renderCell(column, item)}
                          </TableCellContent>
                        </BodyTableCellComponent>
                      ))}
                    </TableBodyRowRoot>
                  );
                })}
              </TableBody>
            </TabularTable>
          </CustomScrollbar>
        </TableContainerRoot>
        {showPagination && (
          <TablePagination
            // ActionsComponent={Pagination}
            rowsPerPageOptions={[rowsPerPage]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
          />
        )}
      </RootComponent>
      {showCheckBox && showSelectionSummary && totalCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 40 }}>

          <SelectionSummary variant="body2">
            <SelectionCount>
              {selectedCount} of {totalCount}
            </SelectionCount>
            <SelectionLabel> selected</SelectionLabel>
          </SelectionSummary>

          {showButton && (
          <DropButton
            onClick={() => handleDropButtonClick?.(selectedRows)}
            disabled={isDropButtonDisabled}
          >
            {dropButtonLabel}
          </DropButton>
          )}
        </div>
      )}
    </ThemeProvider>
  );
};

Tabular.propTypes = {
  title: PropTypes.string,
  textInfo: PropTypes.shape({
    tooltip: PropTypes.shape({
      selectionDisabled: PropTypes.string,
      allItemsSelectionDisabled: PropTypes.string
    })
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    render: PropTypes.func.isRequired,
    primarySearch: PropTypes.bool,
    primarySearchFields: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string
    ]),
    secondarySearch: PropTypes.bool,
    secondarySearchFields: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string
    ]),
    field: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    isSortable: PropTypes.bool,
    sort: PropTypes.array, // [field, type]
    cellClassName: PropTypes.string,
    info: PropTypes.shape({
      icon: PropTypes.string,
      tooltip: PropTypes.string,
      onClick: PropTypes.func,
      classes: PropTypes.shape({
        infoIcon: PropTypes.string,
        infoIconButton: PropTypes.string,
        infoIconContainer: PropTypes.string
      })
    })
  })),
  data: PropTypes.array,
  config: PropTypes.shape({
    showPagination: PropTypes.bool,
    showSearch: PropTypes.bool,
    showTitle: PropTypes.bool,
    showCheckBox: PropTypes.bool,
    showColumnDivider: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    searchDataAttributes: dataAttributesPropType,
    searchLabel: PropTypes.string,
    showSelectionSummary: PropTypes.bool,
    showButton: PropTypes.bool,
    filterByText: PropTypes.string,
    inviteStudentsText: PropTypes.string,
    tableClassName: PropTypes.string,
    rowVariant: PropTypes.oneOf(['card'])

  }),
  callback: PropTypes.shape({
    handleDropButtonClick: PropTypes.func,
    onHandleSearch: PropTypes.func,
    onHandleFilter: PropTypes.func
  }),
  onSelect: PropTypes.func,
  onRowClick: PropTypes.func,
  selectionDisableName: PropTypes.string,
  onInit: PropTypes.func,
  isRowDisabled: PropTypes.func,
  components: PropTypes.shape({
    root: PropTypes.elementType,
    tableRowRoot: PropTypes.elementType,
    tableThCell: PropTypes.elementType,
    tableCell: PropTypes.elementType,
    titleContainerRight: PropTypes.elementType,
    sortIcon: PropTypes.elementType,
    paddingCheckbox: PropTypes.elementType,
    filterSelect: PropTypes.elementType
  }),
  classes: PropTypes.shape({
    root: PropTypes.string,
    sortIcon: PropTypes.string,
    titleContainerRight: PropTypes.string,
    paddingCheckbox: PropTypes.string,
    filterSelect: PropTypes.string
  })
};

Tabular.defaultProps = {
  title: '',
  textInfo: {
    tooltip: {
      selectionDisabled: 'Cannot delete: student work in progress',
      allItemsSelectionDisabled: 'Cannot delete: student work in progress'
    }
  },
  columns: [],
  data: [],
  config: {},
  callback: {},
  onSelect: () => {},
  onRowClick: () => {},
  selectionDisableName: 'isStarted',
  onInit: () => {},
  isRowDisabled: () => {},
  components: {},
  classes: {}
};

export default Tabular;
export {
  OverviewTemplate,
  TextTemplate,
  BadgeTemplate,
  ProgressTemplate,
  TextChipTemplate,
  ContentTemplate,
  DateTemplate,
  ComprehensionTemplate,
  Info,
  DropdownTemplate
};
